import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { commonStyles, theme } from '../../../shared/lib/styles';
import { QUESTION_TYPES } from '../lib/quizTypes';
import { saveQuizProgress, getStarsFromScore } from '../lib/sessionProgress';
import { buildAudioposobieTo } from '../lib/audioposobieLink';
import MultipleChoiceQuestion from './questionTypes/MultipleChoiceQuestion';
import TrueFalseQuestion from './questionTypes/TrueFalseQuestion';
import MatchPairsQuestion from './questionTypes/MatchPairsQuestion';
import OrderEventsQuestion from './questionTypes/OrderEventsQuestion';

const QuizEngine = ({ quiz, onExit }) => {
    const total = quiz.questions.length;
    const [index, setIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [answered, setAnswered] = useState(false);
    const [lastCorrect, setLastCorrect] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [selectedValue, setSelectedValue] = useState(null);
    const [finished, setFinished] = useState(false);

    const question = quiz.questions[index];
    const progressPct = finished ? 100 : (index / total) * 100 + (answered ? 100 / total : 0);

    const submitAnswer = (correct, meta = {}) => {
        if (answered) return;
        setAnswered(true);
        setLastCorrect(correct);
        if (meta.selectedIndex !== undefined) setSelectedIndex(meta.selectedIndex);
        if (meta.selectedValue !== undefined) setSelectedValue(meta.selectedValue);
        if (correct) setScore((s) => s + 1);
    };

    const handleNext = () => {
        if (index >= total - 1) {
            const finalScore = score;
            saveQuizProgress(quiz.id, {
                completed: true,
                score: finalScore,
                total,
            });
            setFinished(true);
            return;
        }
        setIndex((i) => i + 1);
        setAnswered(false);
        setLastCorrect(false);
        setSelectedIndex(null);
        setSelectedValue(null);
    };

    const restart = () => {
        setIndex(0);
        setScore(0);
        setAnswered(false);
        setLastCorrect(false);
        setSelectedIndex(null);
        setSelectedValue(null);
        setFinished(false);
    };

    const listenLink = question?.listenLink || quiz.listenLink;

    if (finished) {
        const stars = getStarsFromScore(score, total);
        const hubListen = quiz.listenLink;
        return (
            <div style={styles.wrap}>
                <div style={styles.final}>
                    <div style={styles.stars}>
                        {[1, 2, 3].map((n) => (
                            <span
                                key={n}
                                style={{
                                    fontSize: theme.fontSize['4xl'],
                                    opacity: n <= stars ? 1 : 0.25,
                                }}
                            >
                                ⭐
                            </span>
                        ))}
                    </div>
                    <h2 style={styles.finalTitle}>
                        {stars >= 2 ? 'Отлично!' : stars >= 1 ? 'Молодец!' : 'Попробуй ещё раз!'}
                    </h2>
                    <p style={styles.finalScore}>
                        Правильных ответов: {score} из {total}
                    </p>
                    <div style={styles.finalActions}>
                        <button type="button" onClick={restart} style={styles.primaryBtn}>
                            Ещё раз
                        </button>
                        <button type="button" onClick={onExit} style={styles.secondaryBtn}>
                            К списку викторин
                        </button>
                        {hubListen && (
                            <Link to={buildAudioposobieTo(hubListen)} style={styles.linkBtn}>
                                Послушать в Аудиопособии
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const renderQuestionBody = () => {
        switch (question.type) {
            case QUESTION_TYPES.MULTIPLE_CHOICE:
                return (
                    <MultipleChoiceQuestion
                        question={question}
                        answered={answered}
                        selectedIndex={selectedIndex}
                        onAnswer={(correct, idx) => submitAnswer(correct, { selectedIndex: idx })}
                    />
                );
            case QUESTION_TYPES.TRUE_FALSE:
                return (
                    <TrueFalseQuestion
                        question={question}
                        answered={answered}
                        selectedValue={selectedValue}
                        onAnswer={(correct, val) => submitAnswer(correct, { selectedValue: val })}
                    />
                );
            case QUESTION_TYPES.MATCH_PAIRS:
                return (
                    <MatchPairsQuestion
                        question={question}
                        answered={answered}
                        onAnswer={(correct) => submitAnswer(correct)}
                    />
                );
            case QUESTION_TYPES.ORDER_EVENTS:
                return (
                    <OrderEventsQuestion
                        question={question}
                        answered={answered}
                        onAnswer={(correct) => submitAnswer(correct)}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div style={styles.wrap}>
            <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${Math.min(progressPct, 100)}%` }} />
            </div>
            <p style={styles.counter}>
                Вопрос {index + 1} из {total}
            </p>

            <h2 style={styles.prompt}>{question.prompt}</h2>

            {renderQuestionBody()}

            {answered && (
                <div
                    style={{
                        ...styles.feedback,
                        borderColor: lastCorrect ? theme.colors.success : theme.colors.danger,
                        backgroundColor: lastCorrect
                            ? 'rgba(200, 220, 190, 0.65)'
                            : theme.colors.errorBg,
                    }}
                >
                    <strong>{lastCorrect ? 'Верно! ' : 'Почти! '}</strong>
                    {question.explanation}
                    {listenLink && (
                        <div style={styles.listenHint}>
                            <Link to={buildAudioposobieTo(listenLink)}>
                                🎧 {listenLink.label || `Класс ${listenLink.grade}`}
                            </Link>
                        </div>
                    )}
                </div>
            )}

            {answered && (
                <button type="button" onClick={handleNext} style={styles.primaryBtn}>
                    {index >= total - 1 ? 'Завершить' : 'Дальше'}
                </button>
            )}
        </div>
    );
};

const styles = {
    wrap: {
        maxWidth: 720,
        margin: '0 auto',
    },
    progressBar: {
        height: 8,
        backgroundColor: theme.colors.parchmentDark,
        borderRadius: '999px',
        overflow: 'hidden',
        marginBottom: theme.spacing.lg,
    },
    progressFill: {
        height: '100%',
        backgroundColor: theme.colors.primary,
        transition: 'width 0.3s ease',
    },
    counter: {
        color: theme.colors.textSecondary,
        fontSize: theme.fontSize.sm,
        marginBottom: theme.spacing.md,
    },
    prompt: {
        fontSize: theme.fontSize.xl,
        marginBottom: theme.spacing.xl,
        lineHeight: 1.4,
    },
    feedback: {
        marginTop: theme.spacing.xl,
        padding: theme.spacing.lg,
        borderRadius: theme.borderRadius.lg,
        borderLeft: '4px solid',
        fontSize: theme.fontSize.base,
        lineHeight: 1.5,
    },
    listenHint: {
        marginTop: theme.spacing.sm,
    },
    primaryBtn: {
        ...commonStyles.button,
        ...commonStyles.buttonPrimary,
        marginTop: theme.spacing.lg,
        width: '100%',
        padding: theme.spacing.lg,
        fontSize: theme.fontSize.lg,
    },
    secondaryBtn: {
        ...commonStyles.button,
        ...commonStyles.buttonSecondary,
        width: '100%',
        padding: theme.spacing.md,
        fontSize: theme.fontSize.base,
    },
    linkBtn: {
        display: 'block',
        textAlign: 'center',
        marginTop: theme.spacing.sm,
        color: theme.colors.primary,
        textDecoration: 'none',
        fontWeight: theme.fontWeight.medium,
    },
    final: {
        textAlign: 'center',
        padding: theme.spacing.xxl,
    },
    stars: {
        marginBottom: theme.spacing.lg,
    },
    finalTitle: {
        fontSize: theme.fontSize['3xl'],
        marginBottom: theme.spacing.md,
    },
    finalScore: {
        fontSize: theme.fontSize.lg,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.xl,
    },
    finalActions: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.md,
        maxWidth: 320,
        margin: '0 auto',
    },
};

export default QuizEngine;
